import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import Stack from "@mui/material/Stack";
import Component from "components/Component";
import FilesFileUpload from "components/FilesFileUpload";
import BigLoader from "components/common/BigLoader";
import DownloadTemplateButton from "components/common/DownloadTemplateButton";
import Visible from "components/common/Visible";
import * as config from "config";
import * as labels from "config.labels";
import * as fileUtils from "fileUtils";
import { SOV_TEMPLATE_DOWNLOAD_BUTTON_ENABLED } from "flags";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useDispatch } from "react-redux";
import * as sovService from "services/sovService";
import * as sovStore from "store/SOV";
import { useTranslator } from "store/hooks/staticDataHooks";

const UploadSov = ({ onExtractionUpload, showExtractionMode = false }) => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState("template");

  const { enqueueSnackbar } = useSnackbar();

  const [createSovFile, { isLoading }] = sovService.useCreateSovFileMutation();

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  const onFileDrop = async (fileId, file) => {
    if (mode === "extraction") {
      if (onExtractionUpload) {
        onExtractionUpload(fileId, file?.name);
      }
      return;
    }

    // Template mode - use existing validation path
    const { data: sovFileStatus, error } = await createSovFile({ fileId });
    if (sovFileStatus?.id) {
      dispatch(
        sovStore.updateSOV({
          sovFileId: sovFileStatus.id,
        })
      );
    } else {
      enqueueSnackbar(
        error?.status === 400
          ? `Invalid template file. ${error?.description}`
          : `Error when reading the SOV file. Please contact ${config.SUPPORT_EMAIL}`,
        {
          variant: "error",
          autoHideDuration: 10_000,
        }
      );
    }
  };

  const translateLabel = useTranslator("uiLabels");

  return isLoading ? (
    <Component title={"Parsing SOV ..."}>
      <BigLoader />
    </Component>
  ) : (
    <FilesFileUpload
      onDrop={onFileDrop}
      asAtDateNotRequired
      size={{ xs: 12 }}
      dropMessage={
        mode === "template"
          ? "Drop an SOV template in .xlsx format"
          : "Drop an SOV file for extraction"
      }
      mimeTypes={fileUtils.spreadsheetMimeTypes}
      options={
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          {showExtractionMode && (
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              aria-label={"SOV upload mode"}
              size={"small"}
            >
              <ToggleButton value={"template"} aria-label={"template mode"}>
                {"Template"}
              </ToggleButton>
              <ToggleButton value={"extraction"} aria-label={"extraction mode"}>
                {"Extraction"}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {mode === "template" && (
            <Visible byTag={SOV_TEMPLATE_DOWNLOAD_BUTTON_ENABLED}>
              <DownloadTemplateButton
                templateId={"sov-template"}
                templateName={"SOV_Template"}
                label={translateLabel(labels.SOV_DOWNLOAD_TEMPLATE_BUTTON, {
                  default: "Template",
                })}
                successMessage={translateLabel(
                  labels.SOV_TEMPLATE_DOWNLOAD_SUCCESS,
                  {
                    default: "Template downloaded successfully",
                  }
                )}
                failMessage={translateLabel(
                  labels.SOV_TEMPLATE_DOWNLOAD_FAILURE,
                  {
                    default: `Error downloading template. Please contact ${config.SUPPORT_EMAIL}`,
                  }
                )}
              />
            </Visible>
          )}
        </Stack>
      }
    />
  );
};

export default UploadSov;
